/* Jaana – comportements des sections propres au thème */
(() => {
  if (customElements.get('eb-slider')) return;

  class JaanaSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.eb-slider__track');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dots = this.querySelector('.eb-slider__dots');
      this.prev?.addEventListener('click', () => this.go(this.index() - 1));
      this.next?.addEventListener('click', () => this.go(this.index() + 1));
      this.track.addEventListener('scroll', () => window.requestAnimationFrame(() => this.update()), { passive: true });
      window.addEventListener('resize', () => this.build());
      this.build();
      const speed = parseFloat(this.dataset.autoplay || '0');
      if (speed > 0) {
        this.timer = setInterval(() => {
          if (this.matches(':hover') || this.paused) return;
          this.go(this.index() + 1, true);
        }, speed * 1000);
        this.addEventListener('touchstart', () => (this.paused = true), { passive: true });
      }
      if (this.dataset.center === 'true') {
        requestAnimationFrame(() => this.go(Math.floor(this.slides.length / 2) - 1));
      }
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    perView() {
      if (!this.slides.length) return 1;
      const w = this.slides[0].getBoundingClientRect().width || 1;
      return Math.max(1, Math.round(this.track.clientWidth / w));
    }
    pages() {
      return Math.max(1, this.slides.length - this.perView() + 1);
    }
    index() {
      const step = this.slides.length > 1 ? this.slides[1].offsetLeft - this.slides[0].offsetLeft : 1;
      return Math.round(this.track.scrollLeft / (step || 1));
    }
    go(i, loop) {
      const pages = this.pages();
      if (i >= pages) i = loop || this.dataset.loop === 'true' ? 0 : pages - 1;
      if (i < 0) i = this.dataset.loop === 'true' ? pages - 1 : 0;
      const slide = this.slides[i];
      if (slide) this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft });
    }
    build() {
      if (!this.dots) return this.update();
      const pages = this.pages();
      this.dots.innerHTML = '';
      if (pages > 1) {
        for (let i = 0; i < pages; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'eb-slider__dot';
          b.setAttribute('aria-label', `${i + 1} / ${pages}`);
          b.addEventListener('click', () => this.go(i));
          this.dots.appendChild(b);
        }
      }
      this.update();
    }
    update() {
      const i = this.index();
      const pages = this.pages();
      if (this.dots) Array.from(this.dots.children).forEach((d, n) => d.setAttribute('aria-current', n === i));
      if (this.dataset.loop !== 'true') {
        if (this.prev) this.prev.disabled = i <= 0;
        if (this.next) this.next.disabled = i >= pages - 1;
      }
      const nav = this.querySelector('.eb-slider__nav');
      if (nav) nav.hidden = pages <= 1;
    }
  }
  customElements.define('eb-slider', JaanaSlider);

  /* Avant / après */
  class JaanaBeforeAfter extends HTMLElement {
    connectedCallback() {
      const input = this.querySelector('input');
      const set = () => this.style.setProperty('--pos', `${input.value}%`);
      input.addEventListener('input', set);
      set();
    }
  }
  customElements.define('eb-before-after', JaanaBeforeAfter);

  /* Vidéos des sliders */
  class JaanaVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.btn = this.querySelector('.eb-media-slide__play');
      this.btn?.addEventListener('click', () => this.toggle());
      this.video.addEventListener('click', () => this.toggle());
      if (this.dataset.autoplay === 'true') {
        this.video.muted = true;
        new IntersectionObserver((entries) => {
          entries.forEach((e) => (e.isIntersecting ? this.play() : this.pause()));
        }, { threshold: 0.4 }).observe(this);
      }
    }
    play() {
      this.video.play().catch(() => {});
      this.classList.add('is-playing');
    }
    pause() {
      this.video.pause();
      this.classList.remove('is-playing');
    }
    toggle() {
      if (this.video.paused) {
        if (this.dataset.autoplay !== 'true') this.video.muted = false;
        this.play();
      } else this.pause();
    }
  }
  customElements.define('eb-video', JaanaVideo);

  /* Stories façon Instagram */
  class JaanaStories extends HTMLElement {
    connectedCallback() {
      this.users = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.duration = parseFloat(this.dataset.speed || '6') * 1000;
      this.viewer = this.querySelector('.eb-viewer');
      this.frame = this.viewer.querySelector('.eb-viewer__frame');
      this.media = this.viewer.querySelector('[data-media]');
      this.bars = this.viewer.querySelector('.eb-viewer__bars');
      this.querySelectorAll('[data-user]').forEach((b) =>
        b.addEventListener('click', () => this.open(parseInt(b.dataset.user, 10)))
      );
      this.viewer.querySelector('[data-close]').addEventListener('click', () => this.close());
      this.viewer.querySelector('[data-tap-prev]').addEventListener('click', () => this.step(-1));
      this.viewer.querySelector('[data-tap-next]').addEventListener('click', () => this.step(1));
      this.viewer.addEventListener('click', (e) => e.target === this.viewer && this.close());
      document.addEventListener('keydown', (e) => {
        if (!this.viewer.classList.contains('is-open')) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowRight') this.step(1);
        if (e.key === 'ArrowLeft') this.step(-1);
      });
    }
    open(u) {
      if (!this.users[u] || !this.users[u].stories.length) return;
      this.u = u;
      this.s = 0;
      this.viewer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.show();
    }
    close() {
      cancelAnimationFrame(this.raf);
      this.media.innerHTML = '';
      this.viewer.classList.remove('is-open');
      document.body.style.overflow = '';
    }
    step(d) {
      const user = this.users[this.u];
      this.s += d;
      if (this.s >= user.stories.length) {
        do {
          this.u += 1;
        } while (this.u < this.users.length && !this.users[this.u].stories.length);
        this.s = 0;
        if (this.u >= this.users.length) return this.close();
      } else if (this.s < 0) {
        let p = this.u - 1;
        while (p >= 0 && !this.users[p].stories.length) p -= 1;
        if (p < 0) this.s = 0;
        else {
          this.u = p;
          this.s = this.users[p].stories.length - 1;
        }
      }
      this.show();
    }
    show() {
      cancelAnimationFrame(this.raf);
      const user = this.users[this.u];
      const story = user.stories[this.s];
      this.viewer.querySelector('[data-avatar]').src = user.avatar || '';
      this.viewer.querySelector('[data-avatar]').hidden = !user.avatar;
      this.viewer.querySelector('[data-name]').textContent = user.name;
      this.viewer.querySelector('[data-verified]').hidden = !user.verified;
      this.viewer.querySelector('[data-time]').textContent = story.time || '';
      this.frame.style.setProperty('--story-bg', story.bg || '#282828');
      this.frame.style.setProperty('--fit', story.fit || 'contain');
      this.bars.innerHTML = user.stories.map(() => '<span class="eb-viewer__bar"><i></i></span>').join('');
      const fills = this.bars.querySelectorAll('i');
      fills.forEach((f, n) => (f.style.width = n < this.s ? '100%' : '0'));
      this.media.innerHTML = '';
      let el;
      if (story.video) {
        el = document.createElement('video');
        el.src = story.video;
        el.playsInline = true;
        el.autoplay = true;
        el.muted = false;
        el.addEventListener('ended', () => this.step(1));
        el.play().catch(() => {
          el.muted = true;
          el.play().catch(() => {});
        });
      } else {
        el = document.createElement('img');
        el.src = story.image;
        el.alt = '';
      }
      this.media.appendChild(el);
      const start = performance.now();
      const tick = (now) => {
        let p;
        if (story.video) p = el.duration ? el.currentTime / el.duration : 0;
        else p = (now - start) / this.duration;
        fills[this.s].style.width = `${Math.min(p, 1) * 100}%`;
        if (!story.video && p >= 1) return this.step(1);
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }
  }
  customElements.define('eb-stories', JaanaStories);

  /* Minuteur du panier */
  class JaanaCartTimer extends HTMLElement {
    connectedCallback() {
      const minutes = parseFloat(this.dataset.minutes || '10');
      let end;
      try {
        end = parseInt(sessionStorage.getItem('eb-cart-timer') || '0', 10);
        if (!end || end < Date.now()) {
          end = Date.now() + minutes * 60000;
          sessionStorage.setItem('eb-cart-timer', end);
        }
      } catch (e) {
        end = Date.now() + minutes * 60000;
      }
      const out = this.querySelector('[data-timer]');
      const tick = () => {
        let left = Math.max(0, Math.round((end - Date.now()) / 1000));
        if (left === 0) {
          end = Date.now() + minutes * 60000;
          left = minutes * 60;
        }
        out.textContent = `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
      };
      tick();
      this.int = setInterval(tick, 1000);
    }
    disconnectedCallback() {
      clearInterval(this.int);
    }
  }
  customElements.define('eb-cart-timer', JaanaCartTimer);
})();

/* Ecom Booster – fonctionnalités de vente */
(() => {
  if (customElements.get('eb-tabs')) return;
  const define = (name, cls) => customElements.get(name) || customElements.define(name, cls);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Ajout au panier puis ouverture du panier tiroir (ou de la page panier) */
  async function addToCart(items, checkout) {
    const drawer = document.querySelector('cart-drawer');
    const body = { items };
    if (drawer && !checkout) body.sections = drawer.getSectionsToRender().map((s) => s.id);
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      alert(data.description || data.message || 'Erreur');
      return;
    }
    if (checkout) return (window.location.href = `${root}checkout`);
    if (drawer && data.sections) {
      drawer.classList.remove('is-empty');
      drawer.renderContents({ ...data, id: items[0].id });
    } else window.location.href = `${root}cart`;
  }
  window.ebAddToCart = addToCart;

  define('eb-quick-add', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('button')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;
        await addToCart([{ id: +this.dataset.variant, quantity: +(this.dataset.qty || 1) }], this.dataset.checkout === 'true');
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      });
    }
  });

  /* Onglets */
  define('eb-tabs', class extends HTMLElement {
    connectedCallback() {
      const btns = [...this.querySelectorAll('[role="tab"]')];
      btns.forEach((b) =>
        b.addEventListener('click', () => {
          btns.forEach((x) => {
            x.setAttribute('aria-selected', x === b);
            const panel = this.querySelector(`#${x.getAttribute('aria-controls')}`);
            if (panel) panel.hidden = x !== b;
          });
        })
      );
    }
  });

  /* Compteurs animés */
  const onVisible = (el, fn) => {
    if (!('IntersectionObserver' in window)) return fn();
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        fn();
      }
    }, { threshold: 0.3 });
    io.observe(el);
  };
  define('eb-counter', class extends HTMLElement {
    connectedCallback() {
      const to = parseFloat(this.dataset.to || '0');
      const decimals = (String(this.dataset.to).split('.')[1] || '').length;
      const bar = this.closest('.eb-result')?.querySelector('[data-bar]');
      onVisible(this, () => {
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / 1600, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          this.textContent = (to * eased).toFixed(decimals);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        if (bar) bar.style.width = `${bar.dataset.bar}%`;
      });
    }
  });

  /* Popup promo */
  define('eb-popup', class extends HTMLElement {
    connectedCallback() {
      const key = `eb-popup-${this.dataset.id}`;
      const days = parseFloat(this.dataset.days || '0');
      let seen = 0;
      try {
        seen = parseInt(localStorage.getItem(key) || '0', 10);
      } catch (e) {}
      const preview = this.dataset.preview === 'true';
      if (!preview && seen && Date.now() - seen < days * 864e5) return;
      this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.querySelector('[data-copy]')?.addEventListener('click', (e) => {
        const b = e.currentTarget;
        navigator.clipboard?.writeText(b.dataset.copy);
        const l = b.querySelector('[data-copy-label]');
        if (l) l.textContent = '✓ Copié !';
      });
      if (this.querySelector('.eb-popup__success')) return this.open();
      if (preview) return this.open();
      this.timer = setTimeout(() => this.open(), parseFloat(this.dataset.delay || '5') * 1000);
      if (this.dataset.exit === 'true') {
        this.exit = (e) => e.clientY <= 0 && this.open();
        document.addEventListener('mouseout', this.exit);
      }
      document.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());
    }
    open() {
      if (this.opened) return;
      this.opened = true;
      clearTimeout(this.timer);
      if (this.exit) document.removeEventListener('mouseout', this.exit);
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
    }
    close() {
      this.classList.remove('is-open');
      setTimeout(() => (this.hidden = true), 250);
      try {
        localStorage.setItem(`eb-popup-${this.dataset.id}`, Date.now());
      } catch (e) {}
    }
  });

  /* Compte à rebours */
  define('eb-countdown', class extends HTMLElement {
    connectedCallback() {
      let end;
      if (this.dataset.mode === 'date') {
        end = new Date((this.dataset.end || '').replace(' ', 'T')).getTime();
      } else {
        const key = `eb-cd-${this.dataset.id}`;
        const ms = parseFloat(this.dataset.minutes || '60') * 60000;
        try {
          end = parseInt(localStorage.getItem(key) || '0', 10);
          if (!end || end < Date.now()) {
            end = Date.now() + ms;
            localStorage.setItem(key, end);
          }
        } catch (e) {
          end = Date.now() + ms;
        }
      }
      const f = (n) => String(n).padStart(2, '0');
      const els = ['d', 'h', 'm', 's'].map((k) => this.querySelector(`[data-${k}]`));
      const tick = () => {
        let left = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (!left || isNaN(left)) {
          clearInterval(this.int);
          if (this.dataset.expired) this.innerHTML = `<span>${this.dataset.expired}</span>`;
          else els.forEach((e) => e && (e.textContent = '00'));
          return;
        }
        const d = Math.floor(left / 86400);
        left %= 86400;
        const vals = [d, Math.floor(left / 3600), Math.floor((left % 3600) / 60), left % 60];
        els.forEach((e, i) => e && (e.textContent = f(vals[i])));
        if (els[0]) els[0].parentElement.hidden = d === 0;
      };
      tick();
      this.int = setInterval(tick, 1000);
    }
    disconnectedCallback() {
      clearInterval(this.int);
    }
  });

  /* Pack de produits */
  define('eb-bundle', class extends HTMLElement {
    connectedCallback() {
      this.money = (c) => (window.Shopify && Shopify.formatMoney ? Shopify.formatMoney(c) : `${(c / 100).toLocaleString('fr-FR')} ${window.Shopify?.currency?.active || ''}`);
      this.querySelectorAll('.eb-bundle__variant').forEach((sel) =>
        sel.addEventListener('change', () => {
          const o = sel.selectedOptions[0];
          const cb = sel.closest('label').querySelector('input');
          cb.value = o.value;
          cb.dataset.price = o.dataset.price;
          cb.dataset.compare = o.dataset.compare;
          sel.closest('label').querySelector('.eb-bundle__price').textContent = this.money(+o.dataset.price);
          this.update();
        })
      );
      this.querySelectorAll('input[type=checkbox]').forEach((c) => c.addEventListener('change', () => this.update()));
      this.querySelector('[data-add]').addEventListener('click', async (e) => {
        const items = [...this.querySelectorAll('input[type=checkbox]:checked:not(:disabled)')].map((c) => ({ id: +c.value, quantity: 1 }));
        if (!items.length) return;
        e.currentTarget.disabled = true;
        await addToCart(items, this.dataset.checkout === 'true');
        e.currentTarget.disabled = false;
      });
      this.update();
    }
    update() {
      const checked = [...this.querySelectorAll('input[type=checkbox]:checked:not(:disabled)')];
      const sum = checked.reduce((t, c) => t + +c.dataset.price, 0);
      const compare = checked.reduce((t, c) => t + Math.max(+c.dataset.compare, +c.dataset.price), 0);
      const off = parseFloat(this.dataset.discount || '0');
      const total = Math.round((sum * (100 - off)) / 100);
      this.querySelector('[data-total]').textContent = this.money(total);
      this.querySelector('[data-compare]').textContent = compare > total ? this.money(compare) : '';
    }
  });

  /* Offres par quantité */
  define('eb-qty-breaks', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      if (!this.form) return;
      this.ids = JSON.parse(this.dataset.variants || '[]');
      this.prices = JSON.parse(this.dataset.prices || '[]');
      this.compares = JSON.parse(this.dataset.compare || '[]');
      this.money = (c) => (window.Shopify && Shopify.formatMoney ? Shopify.formatMoney(c) : `${(c / 100).toLocaleString('fr-FR')} ${window.Shopify?.currency?.active || ''}`);
      this.querySelectorAll('input[type=radio]').forEach((r) => r.addEventListener('change', () => this.apply()));
      this.form.addEventListener('change', () => setTimeout(() => this.prices_update(), 50));
      document.addEventListener('change', (e) => e.target.closest('variant-selects') && setTimeout(() => this.prices_update(), 150));
      this.apply();
    }
    qtyInput() {
      let input = this.form.querySelector('input[name="quantity"]') || document.querySelector(`input[name="quantity"][form="${this.form.id}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        this.form.appendChild(input);
      }
      return input;
    }
    apply() {
      const r = this.querySelector('input[type=radio]:checked');
      if (!r) return;
      this.querySelectorAll('.eb-break').forEach((l) => l.classList.toggle('is-selected', l.contains(r)));
      const input = this.qtyInput();
      input.value = r.value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    prices_update() {
      const idInput = this.form.querySelector('[name="id"]');
      const i = this.ids.indexOf(+(idInput && idInput.value));
      if (i < 0) return;
      const price = this.prices[i];
      const unitFull = Math.max(this.compares[i] || 0, price);
      const img = (JSON.parse(this.dataset.images || '[]'))[i];
      if (img) this.querySelectorAll('[data-break-img] img').forEach((el) => { el.src = img; el.removeAttribute('srcset'); });
      this.querySelectorAll('.eb-break').forEach((l) => {
        const r = l.querySelector('input');
        const qty = +r.value;
        const total = Math.round((price * qty * (100 - +r.dataset.off)) / 100);
        l.querySelector('[data-total]').textContent = this.money(total);
        const full = l.querySelector('[data-full]');
        if (full) full.textContent = unitFull * qty > total ? this.money(unitFull * qty) : '';
      });
    }
  });

  /* Commande WhatsApp */
  define('eb-whatsapp-order', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('button').addEventListener('click', () => {
        const form = document.getElementById(this.dataset.form);
        const id = +(form?.querySelector('[name="id"]')?.value || 0);
        const ids = JSON.parse(this.dataset.ids || '[]');
        const titles = JSON.parse(this.dataset.variants || '[]');
        const variant = titles[ids.indexOf(id)] || '';
        const qty = form?.querySelector('[name="quantity"]')?.value || document.querySelector(`[name="quantity"][form="${this.dataset.form}"]`)?.value || 1;
        const msg = (this.dataset.message || '')
          .replace('[product]', this.dataset.title)
          .replace('[variant]', variant === 'Default Title' ? '' : variant)
          .replace('[qty]', qty)
          .replace('[url]', this.dataset.url);
        window.open(`https://wa.me/${this.dataset.phone}?text=${encodeURIComponent(msg)}`, '_blank');
      });
    }
  });

  /* Produit complémentaire ajouté avec le produit principal */
  define('eb-upsell', class extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.form);
      const cb = this.querySelector('input');
      if (!form || !cb) return;
      form.addEventListener(
        'submit',
        async (e) => {
          if (!cb.checked || form.dataset.ebUpsellDone === 'true') return;
          e.preventDefault();
          e.stopImmediatePropagation();
          try {
            await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: +cb.value, quantity: 1 }] }),
            });
          } catch (err) {}
          form.dataset.ebUpsellDone = 'true';
          form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
          setTimeout(() => (form.dataset.ebUpsellDone = ''), 1000);
        },
        true
      );
    }
  });

  /* Barre d'achat fixe */
  define('eb-sticky-atc', class extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.form);
      const target = form?.querySelector('[name="add"]') || form;
      if (!target) return;
      this.hidden = false;
      new IntersectionObserver(([e]) => {
        const below = e.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', !e.isIntersecting && below);
      }).observe(target);
      document.addEventListener('change', (e) => {
        if (!e.target.closest('variant-selects')) return;
        setTimeout(() => {
          const price = document.querySelector(`#price-${this.dataset.form.replace('product-form-', '')} .price-item--sale, #price-${this.dataset.form.replace('product-form-', '')} .price-item--regular`);
          if (price) this.querySelector('.eb-sticky__price strong').textContent = price.textContent.trim();
        }, 400);
      });
    }
  });

  /* Lecteur de musique */
  define('eb-music', class extends HTMLElement {
    connectedCallback() {
      const audio = this.querySelector('audio');
      const btn = this.querySelector('button');
      const toggle = () => {
        if (audio.paused) audio.play().then(() => this.classList.add('is-playing')).catch(() => {});
        else {
          audio.pause();
          this.classList.remove('is-playing');
        }
      };
      btn.addEventListener('click', toggle);
      if (this.dataset.autoplay === 'true') {
        const first = () => {
          document.removeEventListener('click', first, true);
          if (audio.paused) toggle();
        };
        document.addEventListener('click', first, true);
      }
    }
  });

  /* Parallax + bouton retour en haut */
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const parallax = () => {
    if (!reduced) document.querySelectorAll('[data-parallax]').forEach((el) => {
      const k = parseFloat(el.dataset.parallax || '0') / 100;
      const r = el.parentElement.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      el.style.transform = `translate3d(0, ${(-r.top * k).toFixed(1)}px, 0)`;
    });
    const top = document.querySelector('.eb-totop');
    if (top) top.hidden = window.scrollY < 600;
  };
  window.addEventListener('scroll', () => requestAnimationFrame(parallax), { passive: true });
  document.addEventListener('DOMContentLoaded', parallax);
})();

/* Code promo dans le panier (API panier Shopify, paramètre « discount ») */
(() => {
  if (customElements.get('eb-cart-discount')) return;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  customElements.define('eb-cart-discount', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('form').addEventListener('submit', (e) => {
        e.preventDefault();
        const code = this.querySelector('input').value.trim();
        if (code) this.apply([...this.current(), code]);
      });
      this.querySelectorAll('[data-remove]').forEach((b) =>
        b.addEventListener('click', () => this.apply(this.current().filter((c) => c !== b.dataset.remove)))
      );
    }
    current() {
      return [...this.querySelectorAll('[data-remove]')].map((b) => b.dataset.remove);
    }
    async apply(codes) {
      const msg = this.querySelector('.eb-discount__msg');
      const drawer = document.querySelector('cart-drawer');
      const inDrawer = !!this.closest('cart-drawer');
      const body = { discount: codes.join(',') };
      if (inDrawer && drawer) body.sections = drawer.getSectionsToRender().map((s) => s.id);
      msg.textContent = '…';
      try {
        const res = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        const last = codes[codes.length - 1];
        const found = (data.discount_codes || []).find((d) => last && d.code.toLowerCase() === last.toLowerCase());
        if (last && found && !found.applicable) msg.textContent = 'Ce code ne s’applique pas à ce panier.';
        if (inDrawer && drawer && data.sections) drawer.renderContents({ ...data, id: null });
        else window.location.reload();
      } catch (e) {
        msg.textContent = 'Erreur, réessayez.';
      }
    }
  });
})();

/* Diaporama plein écran */
(() => {
  if (customElements.get('eb-hero')) return;
  customElements.define('eb-hero', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.eb-hero__slide')];
      this.dots = [...this.querySelectorAll('[data-go]')];
      this.i = 0;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.i - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.i + 1, true));
      this.dots.forEach((d) => d.addEventListener('click', () => this.go(+d.dataset.go, true)));
      let x0 = null;
      this.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.go(this.i + (dx < 0 ? 1 : -1), true);
        x0 = null;
      });
      this.addEventListener('shopify:block:select', (e) => this.go(this.slides.findIndex((s) => s.contains(e.target)), true));
      this.start();
    }
    start() {
      clearInterval(this.timer);
      if (this.dataset.autoplay === 'true' && this.slides.length > 1) {
        this.timer = setInterval(() => this.go(this.i + 1), parseFloat(this.dataset.speed || '6') * 1000);
      }
    }
    go(n, user) {
      if (n < 0) n = this.slides.length - 1;
      if (n >= this.slides.length) n = 0;
      this.slides[this.i].classList.remove('is-active');
      this.slides[n].classList.add('is-active');
      this.dots.forEach((d, k) => d.setAttribute('aria-current', k === n));
      this.i = n;
      if (user) this.start();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
  });
})();

/* Produits récemment consultés (mémorisés dans le navigateur du visiteur) */
(() => {
  const KEY = 'eb-recently-viewed';
  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || '[]');
    } catch (e) {
      return [];
    }
  };
  const handle = window.ShopifyAnalytics?.meta?.product?.id && document.querySelector('[data-product-handle]')?.dataset.productHandle;
  const fromUrl = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
  const current = handle || fromUrl;
  if (current) {
    const list = [current, ...read().filter((h) => h !== current)].slice(0, 12);
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {}
  }
  if (customElements.get('eb-recently-viewed')) return;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const money = (c) => (window.Shopify && Shopify.formatMoney ? Shopify.formatMoney(c) : `${(c / 100).toLocaleString('fr-FR')} ${window.Shopify?.currency?.active || ''}`);
  customElements.define('eb-recently-viewed', class extends HTMLElement {
    async connectedCallback() {
      const skip = this.dataset.current || current;
      const handles = read().filter((h) => h !== skip).slice(0, parseInt(this.dataset.limit || '4', 10));
      if (!handles.length) return;
      const products = (await Promise.all(handles.map((h) => fetch(`${root}products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)))).filter(Boolean);
      if (!products.length) return;
      const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
      this.querySelector('.eb-rv').innerHTML = products
        .map((p) => {
          const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=500` : '';
          const cmp = p.compare_at_price > p.price ? ` <s>${money(p.compare_at_price)}</s>` : '';
          return `<a class="eb-rv__item" href="${esc(p.url)}">${img ? `<img src="${esc(img)}" alt="${esc(p.title)}" loading="lazy">` : ''}<span class="eb-rv__title">${esc(p.title)}</span><span class="eb-rv__price">${money(p.price)}${cmp}</span></a>`;
        })
        .join('');
      this.hidden = false;
    }
  });
})();

/* Licence du thème : la boutique doit être activée avec une clé valable, sinon rien ne s'affiche
   (ni la boutique, ni l'aperçu dans l'éditeur). L'activation se fait une seule fois, sur l'écran
   affiché à l'ouverture du site. Si le serveur de licences ne répond pas, la boutique n'est pas bloquée. */
(() => {
  const API = 'https://ecom-booster.netlify.app/eb-api/licence';
  const cfg = window.ebLicence || { key: '', shop: (window.Shopify && Shopify.shop) || '', editor: false };
  if (!cfg.shop || window.ebLicenceChecked) return;
  window.ebLicenceChecked = true;
  const html = document.documentElement;
  const cacheKey = `eb-lic-${cfg.shop}`;
  const MESSAGES = {
    missing: 'Saisis ta clé de licence pour activer le thème sur cette boutique.',
    unknown: 'Cette clé de licence n’existe pas.',
    revoked: 'Cette licence a été suspendue. Contacte Ecom Booster.',
    used: 'Cette clé est déjà utilisée sur une autre boutique.',
    error: 'Vérification impossible pour le moment, réessaie.',
  };
  const save = (valid) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify({ valid, t: Date.now() }));
    } catch (e) {}
  };
  const unlock = () => {
    html.classList.remove('eb-lic-pending');
    document.getElementById('eb-licence-notice')?.remove();
  };
  const check = (key) =>
    fetch(`${API}?key=${encodeURIComponent(key || '')}&shop=${encodeURIComponent(cfg.shop)}`, { mode: 'cors' })
      .then((r) => r.json())
      .then((d) => (d && d.ok === true ? d : { network: true }))
      .catch(() => ({ network: true }));
  const lock = (reason) => {
    html.classList.add('eb-lic-pending');
    const run = () => {
      let box = document.getElementById('eb-licence-notice');
      if (!box) {
        box = document.createElement('div');
        box.id = 'eb-licence-notice';
        box.className = 'eb-lic';
        box.setAttribute('role', 'dialog');
        box.setAttribute('aria-modal', 'true');
        box.innerHTML = `<form class="eb-lic__box" novalidate>
            <strong>Activer le thème Ecom Booster</strong>
            <p data-msg></p>
            <input type="text" name="key" placeholder="EB-XXXX-XXXX-XXXX-XXXX" autocomplete="off" spellcheck="false" aria-label="Clé de licence">
            <button type="submit">Activer</button>
            <small>Boutique : ${cfg.shop.replace(/[<>&"]/g, '')}</small>
          </form>`;
        html.appendChild(box);
        box.querySelector('form').addEventListener('submit', async (e) => {
          e.preventDefault();
          const input = box.querySelector('input');
          const btn = box.querySelector('button');
          const key = input.value.trim();
          if (!key) return input.focus();
          btn.disabled = true;
          btn.textContent = 'Vérification…';
          const d = await check(key);
          btn.disabled = false;
          btn.textContent = 'Activer';
          if (d.valid) {
            save(true);
            unlock();
          } else box.querySelector('[data-msg]').textContent = MESSAGES[d.network ? 'error' : d.reason] || MESSAGES.unknown;
        });
      }
      box.querySelector('[data-msg]').textContent = MESSAGES[reason] || MESSAGES.missing;
      box.querySelector('input').focus();
    };
    document.body ? run() : document.addEventListener('DOMContentLoaded', run);
  };
  let cached = null;
  try {
    cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
  } catch (e) {}
  // Déjà validée dans ce navigateur : on affiche tout de suite, mais on revérifie à chaque chargement
  // pour qu'une licence suspendue bloque la boutique immédiatement.
  if (cached && cached.valid && Date.now() - cached.t < 12 * 3600e3) unlock();
  check(cfg.key).then((d) => {
    if (d.network) return unlock(); // serveur injoignable : on ne bloque pas la boutique
    if (d.valid) {
      save(true);
      return unlock();
    }
    save(false);
    lock(d.reason);
  });
})();

/* Apparition des sections Ecom Booster au défilement (les sections Dawn ont déjà leur animation) */
(() => {
  const body = document.body;
  if (!body) return;
  const sections = document.querySelectorAll('.shopify-section:not(.shopify-section-group-header-group):not(.shopify-section-group-footer-group)');
  const showAll = () => sections.forEach((sec) => sec.firstElementChild?.classList.add('is-revealed'));
  if (body.classList.contains('eb-reveal-none') || !('IntersectionObserver' in window)) return showAll();
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.Shopify?.designMode) return showAll();
  const ITEMS = '.eb-review, .eb-plan, .eb-iconbar__item, .eb-feature, .eb-pf__point, .eb-result, .eb-bundle__item, .eb-fb, .eb-story, .eb-break, .eb-column, .eb-table tbody tr';
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-revealed');
      io.unobserve(e.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
  sections.forEach((sec) => {
    const target = sec.firstElementChild;
    if (!target) return;
    if (sec.querySelector('.scroll-trigger') || sec.querySelector('.eb-divider, .eb-hero, .eb-ticker, .eb-popup, .eb-music, .eb-sticky') || target.getBoundingClientRect().top < window.innerHeight * 0.85) {
      target.classList.add('is-revealed'); // déjà visible ou section animée autrement
      return;
    }
    target.classList.add('eb-reveal-init');
    target.querySelectorAll(ITEMS).forEach((el, i) => {
      el.classList.add('eb-reveal-item');
      el.style.setProperty('--eb-delay', `${Math.min(i, 8) * 0.08 + 0.15}s`);
    });
    io.observe(target);
  });
})();

/* Favoris (gardés dans le navigateur du visiteur) + emballage cadeau */
(() => {
  if (customElements.get('eb-wishlist-page')) return;
  const KEY = 'eb-wishlist';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || '[]');
    } catch (e) {
      return [];
    }
  };
  const write = (l) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(l.slice(0, 60)));
    } catch (e) {}
  };
  const paint = () => {
    const list = read();
    document.querySelectorAll('[data-eb-wish]').forEach((b) => {
      const on = list.includes(b.dataset.ebWish);
      b.setAttribute('aria-pressed', on);
      b.setAttribute('aria-label', on ? 'Retirer des favoris' : 'Ajouter aux favoris');
    });
    document.querySelectorAll('[data-eb-wish-count]').forEach((c) => (c.textContent = list.length || ''));
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-eb-wish]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    const h = b.dataset.ebWish;
    const list = read();
    write(list.includes(h) ? list.filter((x) => x !== h) : [h, ...list]);
    b.classList.remove('is-pop');
    void b.offsetWidth;
    b.classList.add('is-pop');
    paint();
    document.querySelector('eb-wishlist-page')?.render();
  }, true);
  document.addEventListener('DOMContentLoaded', paint);
  document.addEventListener('shopify:section:load', paint);
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      paint();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
  paint();

  const money = (c) => (window.Shopify && Shopify.formatMoney ? Shopify.formatMoney(c) : `${(c / 100).toLocaleString('fr-FR')} ${window.Shopify?.currency?.active || ''}`);
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  customElements.define('eb-wishlist-page', class extends HTMLElement {
    connectedCallback() {
      this.render();
    }
    async render() {
      const handles = read();
      const box = this.querySelector('[data-list]');
      const empty = this.querySelector('[data-empty]');
      if (!handles.length) {
        box.innerHTML = '';
        empty.hidden = false;
        return;
      }
      empty.hidden = true;
      const products = (await Promise.all(handles.map((h) => fetch(`${root}products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)))).filter(Boolean);
      box.innerHTML = products
        .map((p) => {
          const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=500` : '';
          const cmp = p.compare_at_price > p.price ? ` <s>${money(p.compare_at_price)}</s>` : '';
          return `<div class="eb-rv__item eb-wish-item"><button type="button" class="eb-wish eb-wish--card" data-eb-wish="${esc(p.handle)}" aria-pressed="true" aria-label="Retirer des favoris"><svg viewBox="0 0 24 24"><path d="M12 20s-7.5-4.6-9-9.3C2 7.4 4.2 4.5 7.3 4.5c2 0 3.6 1.2 4.7 2.8 1.1-1.6 2.7-2.8 4.7-2.8 3.1 0 5.3 2.9 4.3 6.2C19.5 15.4 12 20 12 20z"/></svg></button><a href="${esc(p.url)}">${img ? `<img src="${esc(img)}" alt="${esc(p.title)}" loading="lazy">` : ''}<span class="eb-rv__title">${esc(p.title)}</span><span class="eb-rv__price">${money(p.price)}${cmp}</span></a></div>`;
        })
        .join('');
    }
  });

  customElements.define('eb-gift-wrap', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('input').addEventListener('change', async (e) => {
        const on = e.target.checked;
        const v = +(this.dataset.variant || 0);
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer && this.closest('cart-drawer') ? drawer.getSectionsToRender().map((s) => s.id) : undefined;
        e.target.disabled = true;
        let res;
        if (v) {
          res = await fetch(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates: { [v]: on ? 1 : 0 }, sections }) });
        } else {
          res = await fetch(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: { 'Emballage cadeau': on ? 'Oui' : '' }, sections }) });
        }
        const data = await res.json().catch(() => ({}));
        e.target.disabled = false;
        if (v) {
          if (sections && data.sections) drawer.renderContents({ ...data, id: null });
          else window.location.reload();
        }
      });
    }
  });
})();

/* Mots qui changent (effet machine à écrire) */
(() => {
  if (customElements.get('eb-typing')) return;
  customElements.define('eb-typing', class extends HTMLElement {
    connectedCallback() {
      this.words = (this.dataset.words || '').split(',').map((w) => w.trim()).filter(Boolean);
      if (this.words.length < 2 && this.dataset.typing !== 'true') return;
      this.i = 0;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.dataset.typing === 'true' ? this.type() : (this.timer = setInterval(() => this.swap(), 2600));
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      clearTimeout(this.t);
    }
    swap() {
      this.i = (this.i + 1) % this.words.length;
      this.animate([{ opacity: 0, transform: 'translateY(0.4em)' }, { opacity: 1, transform: 'none' }], { duration: 450, easing: 'ease-out' });
      this.textContent = this.words[this.i];
    }
    type() {
      this.classList.add('is-typing');
      const word = this.words[this.i];
      let n = word.length, deleting = true;
      const step = () => {
        if (deleting) {
          n--;
          this.textContent = word.slice(0, Math.max(n, 0));
          if (n <= 0) {
            deleting = false;
            this.i = (this.i + 1) % this.words.length;
          }
          return (this.t = setTimeout(step, n <= 0 ? 300 : 40));
        }
        const next = this.words[this.i];
        n++;
        this.textContent = next.slice(0, n);
        if (n >= next.length) return (this.t = setTimeout(() => this.type(), 2200));
        this.t = setTimeout(step, 75);
      };
      this.t = setTimeout(step, 2200);
    }
  });
})();
